import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { IMAGES, LINKS, TOLL_FREE_DISPLAY, TOLL_FREE_HREF, TOLL_FREE_NUMERIC } from '../data/site';
const autoLinks: {
  label: string;
  href: string;
}[] = [{
  label: 'Auto Glass',
  href: LINKS.auto
}, {
  label: 'Windshield Replacement',
  href: LINKS.windshield
}, {
  label: 'Windshield Chip Repair',
  href: LINKS.chip
}, {
  label: 'ADAS Calibration',
  href: LINKS.adas
}, {
  label: 'Emergency Auto Glass',
  href: LINKS.emergencyAuto
}];
const mainLinks: {
  label: string;
  href: string;
}[] = [{
  label: 'Residential Glass',
  href: LINKS.residential
}, {
  label: 'Glass Shower Doors',
  href: LINKS.shower
}, {
  label: 'Commercial Glass',
  href: LINKS.commercial
}, {
  label: 'Resources',
  href: LINKS.resources
}];
export function Header() {
  const [autoOpen, setAutoOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAutoOpen, setMobileAutoOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const utilityRef = useRef<HTMLDivElement>(null);
  const autoWrapRef = useRef<HTMLDivElement>(null);
  const autoButtonRef = useRef<HTMLButtonElement>(null);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);
  const mobilePanelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const header = headerRef.current;
    const utility = utilityRef.current;
    if (!header || !utility) return;

    // Let the description scroll away without guessing its wrapped height.
    const updateOffset = () => {
      header.style.setProperty('--utility-height', `${utility.getBoundingClientRect().height}px`);
    };
    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    observer.observe(utility);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!autoOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAutoOpen(false);
        autoButtonRef.current?.focus();
      }
    };
    const onClick = (e: MouseEvent) => {
      if (!autoWrapRef.current?.contains(e.target as Node)) setAutoOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [autoOpen]);
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        mobileButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const first = mobilePanelRef.current?.querySelector<HTMLElement>('a, button');
    first?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);
  const closeMobile = () => {
    setMobileOpen(false);
    setMobileAutoOpen(false);
  };
  return <header ref={headerRef} className="sticky top-[calc(-1*var(--utility-height,52px))] z-50 bg-white sm:top-0" data-id="element-104" data-mp="2xznn05s4zngn">
      <div ref={utilityRef} className="bg-kosin-charcoal text-white" data-id="element-105" data-mp="hfldqlf7z3ve">
        <div className="mx-auto flex max-w-shell items-center justify-between gap-4 px-[18px] py-2 text-[12px] sm:px-6 sm:text-[13px]" data-id="element-106" data-mp="hhegvqqmj5yn">
          <p className="max-w-[20rem] text-[13px] leading-snug sm:max-w-none" data-id="element-107" data-mp="re12wiuos329">
            Auto Glass, Windshield Replacement &amp; Commercial Glass in
            Southeast Michigan
          </p>
          <div className="flex items-center gap-6" data-id="element-108" data-mp="106bdi19huak1">
            <p className="hidden whitespace-nowrap text-white/75 md:block" data-id="element-109" data-mp="105jlf0f3uuyi">Howell • Ann Arbor • Novi</p>
            <a href={TOLL_FREE_HREF} aria-label={`Call toll free ${TOLL_FREE_NUMERIC}`} className="focus-light hidden items-center gap-2 whitespace-nowrap text-[14px] font-bold text-white xl:inline-flex" data-id="element-110" data-mp="1f2kbkzhnehuv">
              <PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-111" data-mp="3bwdqxshgwqjo" />
              {TOLL_FREE_DISPLAY}
              <span className="font-normal text-white/70" data-id="element-112" data-mp="22xlzemj1mohs">
                ({TOLL_FREE_NUMERIC})
              </span>
            </a>
          </div>
        </div>
      </div>

      <div className="border-b border-kosin-line" data-id="element-113" data-mp="2cx4iz1v1k89b">
        <div className="mx-auto flex max-w-shell items-center justify-between gap-3 px-[18px] py-3 sm:gap-6 sm:px-6 lg:py-4" data-id="element-114" data-mp="2cxcuoqffh7ix">
          <a href="#top" className="shrink-0" aria-label="Kosin’s Glass home" onClick={closeMobile} data-id="element-115" data-mp="2gw3u547d9197">
            <img src={IMAGES.logo} alt="Kosin’s Glass" width={227} height={95} className="h-[50px] w-auto sm:h-[58px] lg:h-[70px]" data-id="element-116" data-mp="1p823o0nvqrhz" />
          </a>

          <nav aria-label="Main" className="hidden xl:block" data-id="element-117" data-mp="1y1ovjfoyvalw">
            <ul className="flex items-center gap-5 whitespace-nowrap text-[14px] font-medium text-kosin-ink" data-id="element-118" data-mp="11gwn1ja47v6m">
              <li data-id="element-119" data-mp="15hqa6afqh5yg">
                <a className="hover:text-kosin-red transition-colors duration-150 ease-kosin" href="#top" data-id="element-120" data-mp="15h6qopafq9ib">
                  Home
                </a>
              </li>
              <li className="relative" ref={autoWrapRef} data-id="element-121" data-mp="t1z0pumudx3e">
                <button ref={autoButtonRef} type="button" className="flex items-center gap-1 hover:text-kosin-red transition-colors duration-150 ease-kosin" aria-expanded={autoOpen} aria-haspopup="true" onClick={() => setAutoOpen(v => !v)} data-id="element-122" data-mp="oyty7s2rhver">
                  Auto Glass
                  <ChevronDownIcon className={`h-4 w-4 transition-transform duration-150 ease-kosin ${autoOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-123" data-mp="5atkheczp2sk" />
                </button>
                {autoOpen && <ul className="absolute left-0 top-full z-50 mt-3 w-[264px] border border-kosin-line bg-white py-2 shadow-[0_12px_28px_rgba(22,22,22,0.12)]" data-id="element-124" data-mp="3ng6c2cosl3tx">
                    {autoLinks.map(link => <li key={link.label} data-id="element-125" data-mp="3isgk1q3kkg8y">
                        <a href={link.href} className="block px-4 py-2.5 text-[14px] text-kosin-ink hover:bg-kosin-mist hover:text-kosin-red transition-colors duration-150 ease-kosin" onClick={() => setAutoOpen(false)} data-id="element-126" data-mp="3iq3x0yiyffp1">
                          {link.label}
                        </a>
                      </li>)}
                  </ul>}
              </li>
              {mainLinks.map(link => <li key={link.label} data-id="element-127" data-mp="3rsd41ef0kqxo">
                  <a href={link.href} className="hover:text-kosin-red transition-colors duration-150 ease-kosin" data-id="element-128" data-mp="3rraeb9tfgujc">
                    {link.label}
                  </a>
                </li>)}
            </ul>
          </nav>

          <div className="flex items-center gap-3" data-id="element-129" data-mp="28sm9wic9we1t">
            <a href={TOLL_FREE_HREF} aria-label={`Call toll free ${TOLL_FREE_NUMERIC}`} className="hidden items-center gap-2 whitespace-nowrap text-kosin-charcoal hover:text-kosin-red transition-colors duration-150 ease-kosin sm:flex xl:hidden" data-id="element-130" data-mp="23vdo87rx1rvt">
              <PhoneIcon className="h-4 w-4 shrink-0 text-kosin-red" aria-hidden="true" data-id="element-131" data-mp="1pv2vt0ayk58j" />
              <span className="leading-tight" data-id="element-132" data-mp="1kw87xjy4vqhk">
                <span className="block text-[16px] font-bold" data-id="element-133" data-mp="1kxjhy1i69uh4">
                  {TOLL_FREE_DISPLAY}
                </span>
                <span className="block text-[12px] text-kosin-body" data-id="element-134" data-mp="1ytwpahszsfhv">
                  {TOLL_FREE_NUMERIC}
                </span>
              </span>
            </a>
            <a href="#quote" className="hidden whitespace-nowrap rounded bg-kosin-red px-5 py-2.5 text-[15px] font-semibold text-white hover:bg-kosin-bright transition-colors duration-150 ease-kosin lg:inline-block" data-id="element-135" data-mp="3aa4teno2nyvn">
              Get A Free Quote
            </a>
            <a href={TOLL_FREE_HREF} aria-label={`Call toll free ${TOLL_FREE_NUMERIC}`} title={`Call ${TOLL_FREE_DISPLAY} (${TOLL_FREE_NUMERIC})`} className="flex min-h-11 flex-col justify-center whitespace-nowrap text-kosin-red sm:hidden" data-id="element-136" data-mp="18msb3u6uqdbu">
              <span className="text-[12px] font-bold" data-id="element-137" data-mp="1mcvn7sc0oqnk">{TOLL_FREE_DISPLAY}</span>
              <span className="text-[11px] text-kosin-body" data-id="element-138" data-mp="1rbgsw7a1g2je">{TOLL_FREE_NUMERIC}</span>
            </a>
            <button ref={mobileButtonRef} type="button" className="flex h-11 w-11 items-center justify-center rounded border border-kosin-line text-kosin-charcoal xl:hidden" aria-label={mobileOpen ? 'Close menu' : 'Open menu'} title={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen} aria-controls="mobile-menu" onClick={() => mobileOpen ? closeMobile() : setMobileOpen(true)} data-id="element-139" data-mp="1wcmihw8ej9op">
              {mobileOpen ? <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-140" data-mp="2bemfbx9yrho5" /> : <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-141" data-mp="2bc8k9ofaa5gl" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && <div id="mobile-menu" ref={mobilePanelRef} className="max-h-[calc(100dvh-160px)] overflow-y-auto overscroll-contain border-b border-kosin-line bg-white xl:hidden" data-id="element-142" data-mp="334hbodw8o7mb">
          <nav aria-label="Mobile" className="mx-auto max-w-shell px-[18px] py-4 sm:px-6" data-id="element-143" data-mp="2u39ha6wufqxi">
            <ul className="divide-y divide-kosin-line text-[16px] font-medium text-kosin-ink" data-id="element-144" data-mp="2p5holaodk0s9">
              <li data-id="element-145" data-mp="1irigka1grzkt">
                <a href="#top" className="block py-3" onClick={closeMobile} data-id="element-146" data-mp="1inujj0wt8v1c">
                  Home
                </a>
              </li>
              <li data-id="element-147" data-mp="1d2e7qqix30aj">
                <button type="button" className="flex w-full items-center justify-between py-3" aria-expanded={mobileAutoOpen} onClick={() => setMobileAutoOpen(v => !v)} data-id="element-148" data-mp="1d3z3ukkvqrai">
                  Auto Glass
                  <ChevronDownIcon className={`h-4 w-4 transition-transform duration-150 ease-kosin ${mobileAutoOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-149" data-mp="1i1qrv3ielt9f" />
                </button>
                {mobileAutoOpen && <ul className="pb-3 pl-3" data-id="element-150" data-mp="25r3123dqe1bq">
                    {autoLinks.map(link => <li key={link.label} data-id="element-151" data-mp="20ttbmnqcdpnw">
                        <a href={link.href} className="block py-2 text-[15px] font-normal text-kosin-body" onClick={closeMobile} data-id="element-152" data-mp="bdk9da6dtcyc">
                          {link.label}
                        </a>
                      </li>)}
                  </ul>}
              </li>
              {mainLinks.map(link => <li key={link.label} data-id="element-153" data-mp="28b83zdqp6ge5">
                  <a href={link.href} className="block py-3" onClick={closeMobile} data-id="element-154" data-mp="28a6p0y6k99r5">
                    {link.label}
                  </a>
                </li>)}
            </ul>
            <div className="mt-4 flex flex-col gap-2" data-id="element-155" data-mp="1zdfmq41f0yo4">
              <a href="#quote" className="rounded bg-kosin-red px-5 py-3 text-center text-[15px] font-semibold text-white" onClick={closeMobile} data-id="element-156" data-mp="23dzh4zulto6k">
                Get A Free Quote
              </a>
              <a href={TOLL_FREE_HREF} aria-label={`Call toll free ${TOLL_FREE_NUMERIC}`} className="flex items-center justify-center gap-2 rounded border border-kosin-line px-5 py-3 text-kosin-charcoal" onClick={closeMobile} data-id="element-157" data-mp="3jr6frpd9fgj2">
                <PhoneIcon className="h-4 w-4 shrink-0 text-kosin-red" aria-hidden="true" data-id="element-158" data-mp="3iaajpe04uqwl" />
                <span className="text-[15px] font-bold" data-id="element-159" data-mp="3daxyexowtrw4">
                  Call {TOLL_FREE_DISPLAY}
                </span>
                <span className="text-[13px] text-kosin-body" data-id="element-160" data-mp="38cczd1le9e99">
                  ({TOLL_FREE_NUMERIC})
                </span>
              </a>
            </div>
          </nav>
        </div>}
    </header>;
}